import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, Menu, Popup } from 'semantic-ui-react';

export const DOC_LINKS = [
  { key: 'classrooms', icon: 'users', href: 'https://dcaclab.com/docs/classrooms/' },
  { key: 'assignments', icon: 'tasks', href: 'https://dcaclab.com/docs/classrooms/assignments/' },
  { key: 'tutorials', icon: 'youtube', href: 'https://youtube.com/playlist?list=PLzqxk04syoqIbxD2WYsjI8W4ghoiyKbTW' }
];

// Forum categories, unchanged from the floating feedback button this menu replaces
export const FEEDBACK_ITEMS = [
  { key: 'feedback', icon: 'hand point right', category: 'bugs' },
  { key: 'feature_request', icon: 'idea', category: 'ideas' },
  { key: 'question', icon: 'question', category: 'questions' }
];

export const HELP_FALLBACK = {
  help: 'Help',
  classrooms: 'Classrooms',
  assignments: 'Assignments',
  tutorials: 'Tutorials',
  feedback: 'Feedback',
  feature_request: 'Feature request',
  question: 'Question'
};

// Signed in: remember the intent in a cookie and open the forum in a new tab.
// Guest: ask them to sign in (setTriedToSave(1) shows the sign-in prompt).
export const openForumIntent = (category, is_user_signed_in, setTriedToSave) => {
  if (!is_user_signed_in) {
    setTriedToSave(1);
    return;
  }
  document.cookie = `forum_intent=${category}`;
  window.open(encodeURI("discourse/post_user_intent"), '_blank');
};

const HelpMenu = ({ l, is_user_signed_in, setTriedToSave }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const wasOpen = useRef(false);
  const words = { ...HELP_FALLBACK, ...(l || {}) };

  // The popup renders in a portal at the end of <body>, out of the tab order next to
  // its trigger: move focus into it on open and give it back to the trigger on close.
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      const timer = setTimeout(() => {
        const firstItem = menuRef.current && menuRef.current.querySelector('[role="menuitem"]');
        if (firstItem) firstItem.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      const trigger = document.getElementById('lab_help_button');
      if (trigger) trigger.focus();
    }
  }, [open]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleFeedbackClick = useCallback((category) => {
    setOpen(false);
    openForumIntent(category, is_user_signed_in, setTriedToSave);
  }, [is_user_signed_in, setTriedToSave]);

  return (
    <Popup
      on='click'
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position='bottom right'
      basic
      style={{ padding: 0, background: 'var(--popover)', border: '1px solid var(--border)' }}
      trigger={
        <Button
          id='lab_help_button'
          className="right-section-btn"
          active={open}
          size='mini'
          style={{ marginLeft: '8px', padding: '0.7em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          aria-label={words.help}
          aria-haspopup='menu'
          aria-expanded={open}
          title={words.help}
        >
          <Icon name='question circle outline' size='large' style={{ margin: 0 }} />
        </Button>
      }
    >
      <div ref={menuRef}>
      <Menu vertical secondary role='menu' aria-label={words.help} style={{ margin: 0 }}>
        {DOC_LINKS.map(item => (
          <Menu.Item
            key={item.key}
            as='a'
            role='menuitem'
            href={item.href}
            target='_blank'
            rel='noopener'
            aria-label={words[item.key]}
            onClick={handleClose}
            style={{ color: 'var(--popover-foreground)' }}
          >
            <Icon name={item.icon} />
            {words[item.key]}
          </Menu.Item>
        ))}
        <Menu.Item fitted style={{ borderTop: '1px solid var(--border)', margin: '4px 0', padding: 0 }} aria-hidden='true' />
        {FEEDBACK_ITEMS.map(item => (
          <Menu.Item
            key={item.key}
            as='button'
            type='button'
            role='menuitem'
            aria-label={words[item.key]}
            onClick={() => handleFeedbackClick(item.category)}
            style={{ color: 'var(--popover-foreground)', width: '100%', textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer' }}
          >
            <Icon name={item.icon} />
            {words[item.key]}
          </Menu.Item>
        ))}
      </Menu>
      </div>
    </Popup>
  );
};

export default React.memo(HelpMenu);
